import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Users, UserPlus, Trash2, Shield } from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

const ROLES = ['owner', 'admin', 'editor', 'viewer', 'photo_editor'];

export default function Team() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ email: '', name: '', role: 'editor' });
  const [submitting, setSubmitting] = useState(false);

  const isOwner = (user?.team_role || 'owner') === 'owner';
  const canManage = isOwner || user?.team_role === 'admin' || user?.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/team');
      setMembers(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail?.error
        ? t('team.permissionDenied')
        : (err.response?.data?.detail || t('team.loadError')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onInvite = async (e) => {
    e.preventDefault();
    if (!invite.email) {
      toast.error(t('team.emailRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/team/invite', invite);
      toast.success(t('team.invited'));
      setInvite({ email: '', name: '', role: 'editor' });
      setInviteOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail?.error
        ? t('team.permissionDenied')
        : (err.response?.data?.detail || t('team.inviteError')));
    } finally {
      setSubmitting(false);
    }
  };

  const onRoleChange = async (memberId, role) => {
    try {
      await api.patch(`/team/${memberId}`, { role });
      toast.success(t('team.roleUpdated'));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('team.updateError'));
    }
  };

  const onRemove = async (memberId, label) => {
    if (!window.confirm(t('team.removeConfirm', { name: label }))) return;
    try {
      await api.delete(`/team/${memberId}`);
      toast.success(t('team.removed'));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('team.updateError'));
    }
  };

  const roleColor = (role) => ({
    owner: 'bg-purple-100 text-purple-800 border-purple-200',
    admin: 'bg-blue-100 text-blue-800 border-blue-200',
    editor: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    viewer: 'bg-slate-100 text-slate-700 border-slate-200',
    photo_editor: 'bg-amber-100 text-amber-800 border-amber-200',
  }[role] || 'bg-slate-100 text-slate-700');

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="main-content max-w-5xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <Users className="w-6 h-6 text-emerald-600" /> {t('team.title')}
            </h1>
            <p className="text-sm text-slate-500 mt-1">{t('team.subtitle')}</p>
          </div>
          {canManage && (
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2"><UserPlus className="w-4 h-4" /> {t('team.invite')}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('team.inviteTitle')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={onInvite} className="space-y-4">
                  <div>
                    <Label>{t('team.email')}</Label>
                    <Input type="email" value={invite.email}
                      onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                      required autoComplete="email" />
                  </div>
                  <div>
                    <Label>{t('team.name')}</Label>
                    <Input value={invite.name}
                      onChange={(e) => setInvite({ ...invite, name: e.target.value })}
                      placeholder={t('team.namePlaceholder')} />
                  </div>
                  <div>
                    <Label>{t('team.role')}</Label>
                    <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.filter((r) => isOwner || r !== 'owner').map((r) => (
                          <SelectItem key={r} value={r}>{t(`team.roles.${r}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">{t(`team.roleDesc.${invite.role}`)}</p>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setInviteOpen(false)}>{t('common.cancel')}</Button>
                    <Button type="submit" disabled={submitting}>
                      {submitting ? t('common.loading') : t('team.sendInvite')}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <Card className="p-0 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">{t('common.loading')}…</div>
          ) : members.length === 0 ? (
            <div className="p-8 text-center text-slate-500">{t('team.empty')}</div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left font-medium px-4 py-3">{t('team.member')}</th>
                      <th className="text-left font-medium px-4 py-3">{t('team.email')}</th>
                      <th className="text-left font-medium px-4 py-3">{t('team.status')}</th>
                      <th className="text-left font-medium px-4 py-3">{t('team.role')}</th>
                      <th className="text-right font-medium px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.id} className="border-t border-slate-100">
                        <td className="px-4 py-3">{m.name || m.email.split('@')[0]}</td>
                        <td className="px-4 py-3 text-slate-600">{m.email}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="capitalize">{m.status}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          {canManage && m.role !== 'owner' ? (
                            <Select value={m.role} onValueChange={(v) => onRoleChange(m.id, v)}>
                              <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {ROLES.filter((r) => isOwner || r !== 'owner').map((r) => (
                                  <SelectItem key={r} value={r}>{t(`team.roles.${r}`)}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${roleColor(m.role)}`}>
                              <Shield className="w-3 h-3" /> {t(`team.roles.${m.role}`)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {canManage && m.role !== 'owner' && (
                            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700"
                              onClick={() => onRemove(m.id, m.name || m.email)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-slate-100">
                {members.map((m) => (
                  <div key={m.id} className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{m.name || m.email.split('@')[0]}</div>
                        <div className="text-xs text-slate-500 truncate">{m.email}</div>
                      </div>
                      <Badge variant="outline" className="capitalize text-xs">{m.status}</Badge>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      {canManage && m.role !== 'owner' ? (
                        <Select value={m.role} onValueChange={(v) => onRoleChange(m.id, v)}>
                          <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ROLES.filter((r) => isOwner || r !== 'owner').map((r) => (
                              <SelectItem key={r} value={r}>{t(`team.roles.${r}`)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${roleColor(m.role)}`}>
                          <Shield className="w-3 h-3" /> {t(`team.roles.${m.role}`)}
                        </span>
                      )}
                      {canManage && m.role !== 'owner' && (
                        <Button size="sm" variant="ghost" className="text-red-600"
                          onClick={() => onRemove(m.id, m.name || m.email)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        <Card className="p-5 mt-5 bg-slate-50 border-slate-200">
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-slate-700">
            <Shield className="w-4 h-4" /> {t('team.rolesTitle')}
          </h3>
          <ul className="text-xs text-slate-600 space-y-1.5">
            <li><b>{t('team.roles.owner')}:</b> {t('team.roleDesc.owner')}</li>
            <li><b>{t('team.roles.admin')}:</b> {t('team.roleDesc.admin')}</li>
            <li><b>{t('team.roles.editor')}:</b> {t('team.roleDesc.editor')}</li>
            <li><b>{t('team.roles.viewer')}:</b> {t('team.roleDesc.viewer')}</li>
          </ul>
        </Card>
      </main>
    </div>
  );
}
